/**
 * 🐾 Sovereign Notification Settings & Hub Modal for Fissal Mobile
 * Complete control center for Android notification channels, granular topic
 * toggles, cooldown throttles, live ESO countdowns, and immediate test banners.
 */

import React, { useState, useEffect } from 'react';
import {
  Bell,
  BellOff,
  Clock,
  Shield,
  Coins,
  Swords,
  Video,
  Activity,
  CheckCircle2,
  X,
  Send,
  Trash2,
  Sliders,
  Radio,
  Volume2,
  VolumeX,
  RotateCcw
} from 'lucide-react';
import {
  notificationService,
  NotificationTopic,
  NotificationPreferences,
  PushNotificationPayload,
  DEFAULT_TOPIC_CONFIGS
} from '../services/notificationService';
import { triggerHaptic } from '../services/purrSynthesizer';
import { getServerUrl } from '../services/api';

interface NotificationSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const COOLDOWN_OPTIONS = [
  { value: 0, label: 'Instant (0m)' },
  { value: 1, label: '1 min' },
  { value: 5, label: '5 mins' },
  { value: 15, label: '15 mins' },
  { value: 30, label: '30 mins' },
  { value: 60, label: '1 hour' },
  { value: 360, label: '6 hours' }
];

export const NotificationSettingsModal: React.FC<NotificationSettingsModalProps> = ({ isOpen, onClose }) => {
  const [prefs, setPrefs] = useState<NotificationPreferences>(notificationService.getPreferences());
  const [history, setHistory] = useState<PushNotificationPayload[]>(notificationService.getHistory());
  const [activeTab, setActiveTab] = useState<'topics' | 'history' | 'trader'>('topics');
  const [testSentTopic, setTestSentTopic] = useState<string | null>(null);

  // Live trader countdown from gateway
  const [traderData, setTraderData] = useState<{
    formatted?: string;
    targetIso?: string;
    isFlipDay?: boolean;
    nextFlipUtc?: string;
  } | null>(null);

  // ESO Realm status
  const [esoStatus, setEsoStatus] = useState<Record<string, string>>({});

  useEffect(() => {
    const unsubPrefs = notificationService.subscribe(setPrefs);
    const unsubHist = notificationService.subscribeHistory(setHistory);
    return () => {
      unsubPrefs();
      unsubHist();
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    // Fetch live trader countdown & ESO status from gateway
    const target = getServerUrl();
    fetch(`${target}/api/notifications/trader-timer`, { signal: AbortSignal.timeout(3000) })
      .then(res => res.json())
      .then(data => {
        if (data.ok && data.countdown) setTraderData(data.countdown);
      })
      .catch(() => {});

    fetch(`${target}/api/notifications/eso-status`, { signal: AbortSignal.timeout(3000) })
      .then(res => res.json())
      .then(data => {
        if (data.ok && data.realms) setEsoStatus(data.realms);
      })
      .catch(() => {});
  }, [isOpen]);

  if (!isOpen) return null;

  const handleToggleMaster = () => {
    triggerHaptic([30, 60]);
    notificationService.setMasterEnabled(!prefs.masterEnabled);
  };

  const handleToggleTopic = (topic: NotificationTopic) => {
    triggerHaptic([20, 40]);
    const current = prefs.topics[topic]?.enabled ?? true;
    notificationService.updateTopic(topic, { enabled: !current });
  };

  const handleChangeCooldown = (topic: NotificationTopic, minutes: number) => {
    triggerHaptic([20]);
    notificationService.updateTopic(topic, { cooldownMinutes: minutes });
  };

  const handleTestNotification = async (topic: NotificationTopic) => {
    triggerHaptic([40, 80]);
    setTestSentTopic(topic);
    await notificationService.testNotification(topic);
    setTimeout(() => setTestSentTopic(null), 2500);
  };

  const getTopicIcon = (topic: NotificationTopic) => {
    switch (topic) {
      case 'camera_detections': return <Video className="w-5 h-5 text-sky-400" />;
      case 'trader_timers': return <Coins className="w-5 h-5 text-amber-400" />;
      case 'eso_servers': return <Swords className="w-5 h-5 text-emerald-400" />;
      case 'investing_sentinel': return <Activity className="w-5 h-5 text-purple-400" />;
      case 'system_health': return <Shield className="w-5 h-5 text-rose-400" />;
    }
  };

  const activeCount = Object.values(prefs.topics).filter(t => t.enabled).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="relative w-full max-w-xl max-h-[90vh] bg-slate-900 border border-amber-500/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden text-slate-100">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-950/60">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <Bell className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-wide flex items-center gap-2">
                Notification Deck
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-mono">
                  {prefs.masterEnabled ? `${activeCount}/5 Active` : 'Muted'}
                </span>
              </h2>
              <p className="text-xs text-slate-400">Granular Android channels & cooldown throttles</p>
            </div>
          </div>
          <button
            onClick={() => {
              triggerHaptic([20]);
              onClose();
            }}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-800 bg-slate-950/30 px-6 pt-2 gap-2 text-xs font-semibold">
          <button
            onClick={() => { triggerHaptic([15]); setActiveTab('topics'); }}
            className={`pb-2.5 px-3 border-b-2 transition flex items-center gap-1.5 ${
              activeTab === 'topics'
                ? 'border-amber-400 text-amber-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Sliders className="w-3.5 h-3.5" />
            Topic Channels
          </button>
          <button
            onClick={() => { triggerHaptic([15]); setActiveTab('trader'); }}
            className={`pb-2.5 px-3 border-b-2 transition flex items-center gap-1.5 ${
              activeTab === 'trader'
                ? 'border-amber-400 text-amber-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Coins className="w-3.5 h-3.5" />
            Trader & Realm Radar
          </button>
          <button
            onClick={() => { triggerHaptic([15]); setActiveTab('history'); }}
            className={`pb-2.5 px-3 border-b-2 transition flex items-center gap-1.5 ${
              activeTab === 'history'
                ? 'border-amber-400 text-amber-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            Recent Alerts ({history.length})
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          
          {/* TAB 1: TOPIC CHANNELS */}
          {activeTab === 'topics' && (
            <>
              {/* Master Push Switch */}
              <div className="p-4 rounded-xl bg-slate-950/80 border border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${prefs.masterEnabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
                    {prefs.masterEnabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Master Notification Switch</div>
                    <div className="text-xs text-slate-400">
                      {prefs.masterEnabled ? 'Broadcasting alerts to Pixel 11 Pro XL' : 'All alerts currently paused'}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleToggleMaster}
                  className={`w-12 h-6 rounded-full transition-colors relative p-0.5 ${
                    prefs.masterEnabled ? 'bg-amber-500' : 'bg-slate-700'
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-full bg-white transition-transform ${
                      prefs.masterEnabled ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Topic Cards List */}
              <div className="space-y-3">
                {(Object.keys(DEFAULT_TOPIC_CONFIGS) as NotificationTopic[]).map(topic => {
                  const cfg = prefs.topics[topic] || DEFAULT_TOPIC_CONFIGS[topic];
                  const isEnabled = prefs.masterEnabled && cfg.enabled;

                  return (
                    <div
                      key={topic}
                      className={`p-4 rounded-xl border transition-all ${
                        isEnabled
                          ? 'bg-slate-800/40 border-slate-700 hover:border-slate-600'
                          : 'bg-slate-950/40 border-slate-850 opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-slate-900 border border-slate-800 shrink-0 mt-0.5">
                            {getTopicIcon(topic)}
                          </div>
                          <div>
                            <div className="text-sm font-semibold flex items-center gap-2">
                              {cfg.displayName}
                              {isEnabled ? (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-mono">
                                  {cfg.cooldownMinutes === 0 ? 'INSTANT' : `${cfg.cooldownMinutes}m COOLDOWN`}
                                </span>
                              ) : (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 font-mono">
                                  MUTED
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">{cfg.description}</div>
                          </div>
                        </div>

                        {/* Enable/Disable Toggle */}
                        <button
                          disabled={!prefs.masterEnabled}
                          onClick={() => handleToggleTopic(topic)}
                          className={`w-10 h-5 rounded-full transition-colors relative p-0.5 shrink-0 ${
                            cfg.enabled && prefs.masterEnabled ? 'bg-amber-500' : 'bg-slate-700'
                          }`}
                        >
                          <div
                            className={`w-4 h-4 rounded-full bg-white transition-transform ${
                              cfg.enabled && prefs.masterEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      {/* Controls Row: Cooldown Selector + Sound/Vibe Toggles + Test Button */}
                      <div className="mt-3 pt-3 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-2 text-xs">
                        {/* Cooldown selector & quick reset */}
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-slate-400" />
                          <span className="text-slate-400">Cooldown:</span>
                          <select
                            disabled={!isEnabled}
                            value={cfg.cooldownMinutes}
                            onChange={e => handleChangeCooldown(topic, parseInt(e.target.value, 10))}
                            className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-slate-200 focus:outline-none focus:border-amber-400 disabled:opacity-50 font-mono text-[11px]"
                          >
                            {COOLDOWN_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>

                          {/* Quick Reset Cooldown */}
                          <button
                            disabled={!isEnabled}
                            onClick={() => {
                              triggerHaptic([30, 20]);
                              notificationService.resetCooldown(topic);
                            }}
                            className="p-1.5 rounded-lg bg-slate-900 border border-slate-700 hover:border-amber-400/50 text-slate-400 hover:text-amber-300 transition text-xs disabled:opacity-40"
                            title="Reset active cooldown timer immediately"
                          >
                            <RotateCcw className="w-3 h-3" />
                          </button>
                        </div>

                        {/* Sound & Vibration toggles + Test Ping */}
                        <div className="flex items-center gap-1.5">
                          {/* Sound Toggle */}
                          <button
                            disabled={!isEnabled}
                            onClick={() => {
                              triggerHaptic([20]);
                              notificationService.toggleSound(topic);
                            }}
                            className={`p-1.5 rounded-lg border text-xs transition flex items-center ${
                              cfg.sound
                                ? 'bg-amber-500/20 border-amber-500/40 text-amber-300'
                                : 'bg-slate-900 border-slate-700 text-slate-500'
                            } disabled:opacity-40`}
                            title={cfg.sound ? 'Sound Alert Active' : 'Muted (Silent Channel)'}
                          >
                            {cfg.sound ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                          </button>

                          {/* Vibration Toggle */}
                          <button
                            disabled={!isEnabled}
                            onClick={() => {
                              triggerHaptic([30, 40]);
                              notificationService.toggleVibration(topic);
                            }}
                            className={`p-1.5 rounded-lg border text-xs transition flex items-center ${
                              cfg.vibration
                                ? 'bg-amber-500/20 border-amber-500/40 text-amber-300'
                                : 'bg-slate-900 border-slate-700 text-slate-500'
                            } disabled:opacity-40`}
                            title={cfg.vibration ? 'Haptic Vibration Active' : 'Vibration Disabled'}
                          >
                            <Activity className={`w-3.5 h-3.5 ${cfg.vibration ? 'text-amber-300' : 'text-slate-500 opacity-40'}`} />
                          </button>

                          {/* Test Ping */}
                          <button
                            disabled={!isEnabled}
                            onClick={() => handleTestNotification(topic)}
                            className="px-2.5 py-1 rounded-lg bg-slate-900 border border-slate-700 hover:border-amber-500/50 text-slate-300 hover:text-white transition flex items-center gap-1 disabled:opacity-40"
                          >
                            {testSentTopic === topic ? (
                              <>
                                <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                                <span className="text-emerald-400">Buzzed!</span>
                              </>
                            ) : (
                              <>
                                <Send className="w-3 h-3" />
                                <span>Test</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* TAB 2: TRADER & REALM RADAR */}
          {activeTab === 'trader' && (
            <div className="space-y-4">
              {/* Guild Trader Countdown Card */}
              <div className="p-5 rounded-xl bg-gradient-to-br from-amber-500/10 to-slate-900 border border-amber-500/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-amber-400 font-semibold text-sm">
                    <Coins className="w-5 h-5" />
                    Weekly Guild Trader Flip
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 font-mono">
                    Tuesday 19:00 UTC
                  </span>
                </div>
                <div className="mt-4 text-center py-3 bg-slate-950/60 rounded-xl border border-slate-800">
                  <div className="text-3xl font-black font-mono tracking-wider text-amber-300">
                    {traderData?.formatted || '114h 12m 30s'}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">Countdown to next kiosk reset</div>
                </div>
                <div className="mt-3 text-xs text-slate-400 flex items-center justify-between">
                  <span>Next Reset: {traderData?.nextFlipUtc || 'Every Tuesday 19:00 UTC'}</span>
                  <span>Alert Stages: 24h, 2h, Flip</span>
                </div>
              </div>

              {/* ESO Megaservers Realm Status Card */}
              <div className="p-5 rounded-xl bg-slate-950/80 border border-slate-800 space-y-3">
                <div className="flex items-center justify-between text-sm font-semibold text-emerald-400">
                  <div className="flex items-center gap-2">
                    <Swords className="w-5 h-5" />
                    Official Megaserver Status
                  </div>
                  <span className="text-xs text-slate-400">Live Zenimax Telemetry</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.keys(esoStatus).length > 0 ? (
                    Object.entries(esoStatus).map(([realm, status]) => (
                      <div key={realm} className="p-2.5 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-between">
                        <span className="truncate pr-2 text-slate-300">{realm.replace('The Elder Scrolls Online', 'ESO')}</span>
                        <span className={`font-mono font-bold px-1.5 py-0.5 rounded text-[10px] ${
                          status === 'UP' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300'
                        }`}>
                          {status}
                        </span>
                      </div>
                    ))
                  ) : (
                    <>
                      <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-between">
                        <span className="text-slate-300">ESO (NA)</span>
                        <span className="font-mono font-bold px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/20 text-emerald-300">UP</span>
                      </div>
                      <div className="p-2.5 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-between">
                        <span className="text-slate-300">ESO (EU)</span>
                        <span className="font-mono font-bold px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/20 text-emerald-300">UP</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: RECENT ALERTS HISTORY */}
          {activeTab === 'history' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between pb-2">
                <span className="text-xs text-slate-400">Stored on-device and homelab gateway</span>
                {history.length > 0 && (
                  <button
                    onClick={() => { triggerHaptic([30]); notificationService.clearHistory(); }}
                    className="text-xs text-rose-400 hover:text-rose-300 flex items-center gap-1 transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Clear History
                  </button>
                )}
              </div>

              {history.length === 0 ? (
                <div className="py-12 text-center text-slate-500 text-xs">
                  <BellOff className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  No notifications received yet. Fire a test ping above!
                </div>
              ) : (
                history.map((item, idx) => (
                  <div key={`${item.id}-${idx}`} className="p-3.5 rounded-xl bg-slate-950/60 border border-slate-800 flex items-start gap-3">
                    <div className="text-lg mt-0.5 shrink-0">{item.icon || '🐾'}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-semibold text-slate-200 truncate">{item.title}</div>
                        <div className="text-[10px] text-slate-500 shrink-0">
                          {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <div className="text-xs text-slate-400 mt-1">{item.body}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-800 bg-slate-950/80 flex items-center justify-between text-xs text-slate-400">
          <div className="flex items-center gap-1.5">
            <Radio className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
            <span>Sovereign Homelab Push Channel Active</span>
          </div>
          <button
            onClick={() => {
              triggerHaptic([20]);
              onClose();
            }}
            className="px-4 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold transition"
          >
            Done
          </button>
        </div>

      </div>
    </div>
  );
};
